import React, { useId, useMemo, useState } from 'react';
import { FiAlertTriangle, FiCheckCircle, FiChevronDown, FiInfo, FiLoader } from 'react-icons/fi';
import { useI18n } from '../../../i18n';
import {
  affectedGuardIds,
  type ComplianceIssue,
  type FixOption,
  type RemediationPlan,
  type RosterChangeOperation,
  type RosterSnapshot,
} from '../../../services/rosterCompliance';
import { describeOperation, fixTitle, issueLabel } from './fixPresentation';
import RosterDiff from './RosterDiff';
import type { ReviewCheck } from './useComplianceFixes';

interface ChangeReviewProps {
  plan: RemediationPlan;
  fixes: Array<{ issue: ComplianceIssue; option: FixOption }>;
  operations: RosterChangeOperation[];
  projected: RosterSnapshot;
  review: ReviewCheck;
  applyError: string;
  guardName: (guardId: string) => string;
}

function RuleCheck({ review }: { review: ReviewCheck }) {
  const { t, tn } = useI18n();

  if (review.status === 'checking' || review.status === 'idle') {
    return (
      <div className="cfx-check cfx-check--pending">
        <FiLoader className="cfx-spin" aria-hidden="true" />
        <p className="cfx-check__title">{t('Checking these changes together against the roster rules…')}</p>
      </div>
    );
  }

  const verification = review.verification;
  if (review.status === 'error' || !verification || verification.status === 'failed') {
    return (
      <div className="cfx-check cfx-check--error" role="alert">
        <FiAlertTriangle aria-hidden="true" />
        <div>
          <p className="cfx-check__title">{t('These changes cannot be applied as they stand')}</p>
          <p className="cfx-check__detail">{review.error || verification?.error}</p>
          <p className="cfx-check__detail">{t('The roster may have changed. Go back and refresh the suggestions.')}</p>
        </div>
      </div>
    );
  }

  if (verification.remaining.length === 0) {
    return (
      <div className="cfx-check cfx-check--success">
        <FiCheckCircle aria-hidden="true" />
        <div>
          <p className="cfx-check__title">{t('These changes clear every breach on this roster')}</p>
          <p className="cfx-check__detail">{t('Checked against the same rules that publishing uses.')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="cfx-check cfx-check--warning">
      <FiAlertTriangle aria-hidden="true" />
      <div>
        <p className="cfx-check__title">
          {tn(
            '{count} breach will remain after these changes',
            '{count} breaches will remain after these changes',
            verification.remaining.length,
          )}
        </p>
        <ul className="cfx-check__list">
          {verification.remaining.map((violation, index) => (
            <li key={`${violation.rule_code}-${violation.guard_id}-${index}`}>
              {verification.introduced.includes(violation) && <span className="cfx-pill cfx-pill--warning">{t('New')}</span>}
              {violation.message}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/** The confirmation step: exactly what will change, whether it passes, and the roster before and after. */
export default function ChangeReview({
  plan,
  fixes,
  operations,
  projected,
  review,
  applyError,
  guardName,
}: ChangeReviewProps) {
  const { t, tn } = useI18n();
  const tr = { t, tn };
  const diffId = useId();
  const [showRoster, setShowRoster] = useState(false);

  const guardIds = useMemo(() => affectedGuardIds(operations), [operations]);
  const lookup = useMemo(() => ({ snapshot: plan.snapshot, guardName }), [guardName, plan.snapshot]);

  return (
    <div className="cfx-review">
      <div aria-live="polite">
        <RuleCheck review={review} />
      </div>

      {applyError && (
        <div className="cfx-check cfx-check--error" role="alert">
          <FiAlertTriangle aria-hidden="true" />
          <div>
            <p className="cfx-check__title">{t('Nothing was changed')}</p>
            <p className="cfx-check__detail">{applyError}</p>
          </div>
        </div>
      )}

      <section className="cfx-section" aria-labelledby={`${diffId}-changes`}>
        <h3 id={`${diffId}-changes`} className="cfx-section__title">
          {tn('{count} change to this roster', '{count} changes to this roster', operations.length)}
        </h3>
        <ol className="cfx-changes">
          {fixes.map(({ issue, option }) => (
            <li key={option.id} className="cfx-change">
              <p className="cfx-change__fix">{fixTitle(option, tr)}</p>
              <p className="cfx-change__issue">{t('Fixes: {issue}', { issue: issueLabel(issue, tr) })}</p>
              <ul className="cfx-change__operations">
                {option.operations.map((operation, index) => (
                  <li key={index}>{describeOperation(operation, lookup, tr)}</li>
                ))}
              </ul>
            </li>
          ))}
        </ol>
        <p className="cfx-note">
          <FiInfo aria-hidden="true" />
          {t('Changed duties and breaks go back to draft. Guards see them once you publish the day again.')}
        </p>
      </section>

      <section className="cfx-section">
        <button
          type="button"
          className={`cfx-disclosure${showRoster ? ' is-open' : ''}`}
          aria-expanded={showRoster}
          aria-controls={diffId}
          onClick={() => setShowRoster((open) => !open)}
        >
          <FiChevronDown aria-hidden="true" />
          {showRoster ? t('Hide the affected roster') : t('View the affected roster')}
          <span className="cfx-disclosure__hint">
            {tn('{count} guard', '{count} guards', guardIds.length)}
          </span>
        </button>
        {showRoster && (
          <RosterDiff
            id={diffId}
            before={plan.snapshot}
            after={projected}
            guardIds={guardIds}
            guardName={guardName}
          />
        )}
      </section>
    </div>
  );
}
